import { useCallback, useEffect, useRef, useState } from 'react'
import {
  cmdHistory,
  cmdResponse,
  cmdWatch,
  sendCommand,
  type CmdCommand,
  type CmdPane,
  type CmdSection,
} from '../api/deviceCmd'
import { DeviceCmdSections } from './DeviceCmdSections'

/**
 * Terminalul propriu-zis: Command Prompt Lite, Command Prompt, Power Shell si
 * Terminal (Linux) sunt acelasi panou, cu alt prompt si alta fila in spate.
 *
 * Portul lui `RemoteCommandPrompt` din `public/js/opteemum.js`, fara
 * jquery.terminal: se scrie o linie, ea pleaca spre coada masinii, si raspunsul
 * se citeste inapoi la fiecare secunda, cel mult nouazeci de ori - dupa care
 * scrie ca agentul n-a raspuns. Nu e un shell adevarat: nu se poate raspunde la
 * intrebari si nu se poate derula, de-aia comenzile lungi din coloana din
 * dreapta poarta `--no-pager` sau `head`.
 *
 * Marca `__OD_CWD__=` din coada raspunsului nu se arata: ea spune doar unde a
 * ramas agentul, si muta promptul acolo. Asa `cd /etc` urmat de `ls` listeaza
 * folderul acela.
 */

const POLL_MS = 1000
const POLL_LIMIT = 90
const WATCH_MS = 10000

interface Line {
  key: number
  text: string
  tone: 'out' | 'in' | 'err'
}

export interface DeviceCmdTerminalProps {
  deviceId: number
  pane: CmdPane
  title: string
  greeting: string
  prompt: string
  sections: CmdSection[]
  sectionsNote: string
  linux: boolean
}

/** Textul comenzii cu valoarea pusa unde ii e locul (la coada, sau in semn). */
export function withArgument(command: CmdCommand, value: string): string {
  const prefix = command.command ?? ''
  const slot = command.placeholder ?? ''
  if (slot !== '' && prefix.includes(slot)) {
    return prefix.split(slot).join(value)
  }
  return prefix + value
}

export function DeviceCmdTerminal({
  deviceId,
  pane,
  title,
  greeting,
  prompt,
  sections,
  sectionsNote,
  linux,
}: DeviceCmdTerminalProps) {
  const [lines, setLines] = useState<Line[]>([{ key: 0, text: greeting, tone: 'out' }])
  const [promptText, setPromptText] = useState(prompt)
  const [typed, setTyped] = useState('')
  const [busy, setBusy] = useState(false)
  const [past, setPast] = useState<string[]>([])
  const [pastAt, setPastAt] = useState(-1)
  const seq = useRef(1)
  const alive = useRef(true)
  const box = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    alive.current = true
    return () => {
      alive.current = false
    }
  }, [])

  useEffect(() => {
    if (box.current) box.current.scrollTop = box.current.scrollHeight
  }, [lines])

  /* Cat timp terminalul e deschis, masina e tinuta pe pas scurt - altfel
     agentul isi ia comanda abia la urmatoarea trezire. */
  useEffect(() => {
    if (!pane.can_send) return
    let stop = false
    const ping = () => {
      cmdWatch(deviceId).catch(() => undefined)
    }
    ping()
    const timer = window.setInterval(() => {
      if (!stop) ping()
    }, WATCH_MS)
    return () => {
      stop = true
      window.clearInterval(timer)
    }
  }, [deviceId, pane.can_send])

  const echo = useCallback((text: string, tone: Line['tone'] = 'out') => {
    const clean = applyCwdMark(text)
    if (clean.text === '') {
      if (clean.cwd) setPromptText(`${clean.cwd} $ `)
      return
    }
    if (clean.cwd) setPromptText(`${clean.cwd} $ `)
    setLines((old) => [...old, { key: seq.current++, text: clean.text, tone }])
  }, [])

  const poll = useCallback(
    (since: string) => {
      let tries = 0
      const timer = window.setInterval(async () => {
        tries += 1
        if (!alive.current) {
          window.clearInterval(timer)
          return
        }
        if (tries > POLL_LIMIT) {
          window.clearInterval(timer)
          echo('No response from the agent. Is it online?', 'err')
          setBusy(false)
          return
        }
        try {
          const answer = await cmdResponse(deviceId, since)
          if (answer.success && Array.isArray(answer.msg)) {
            window.clearInterval(timer)
            for (const row of answer.msg) echo(row.message)
            setBusy(false)
          }
        } catch {
          /* o citire picata nu opreste asteptarea: agentul poate raspunde la
             urmatoarea. Limita de nouazeci ramane singurul capat. */
        }
      }, POLL_MS)
    },
    [deviceId, echo],
  )

  const run = useCallback(
    async (body: { cmd: string; cmd_content?: string; arguments?: string }, shown: string) => {
      if (!pane.can_send || busy) return
      setBusy(true)
      echo(promptText + shown, 'in')
      try {
        const answer = await sendCommand(deviceId, { pane: pane.key, ...body })
        if (!answer.success) {
          echo(answer.msg || 'Command was not queued.', 'err')
          setBusy(false)
          return
        }
        echo('Waiting response...')
        poll(answer.responseDate ?? '')
      } catch (e) {
        echo(e instanceof Error && e.message ? e.message : 'Error! Please try again later.', 'err')
        setBusy(false)
      }
    },
    [busy, deviceId, echo, pane.can_send, pane.key, poll, promptText],
  )

  const submit = () => {
    const line = typed.trim()
    setTyped('')
    if (line === '') return
    setPast((old) => [...old, line])
    setPastAt(-1)
    void run({ cmd: 'custom', cmd_content: line }, line)
  }

  /* Butoanele din coloana din dreapta. Cele din catalog trimit ID-UL comenzii,
     nu textul: serverul il rezolva din `it_cmd_commands`, exact cum o facea
     `prepareDinamicCommand`. Asa lista de comenzi ramane a serverului. */
  const runCommand = (command: CmdCommand, value: string | null) => {
    const shown = command.command ? (value === null ? command.command : withArgument(command, value)) : command.display_name
    if (command.cmd_id) {
      void run({ cmd: String(command.cmd_id), ...(value === null ? {} : { arguments: value }) }, shown)
      return
    }
    void run({ cmd: 'custom', cmd_content: shown }, shown)
  }

  const showHistory = async () => {
    echo('Loading history...')
    try {
      const answer = await cmdHistory(deviceId)
      if (answer.success && Array.isArray(answer.msg)) {
        setLines([])
        for (const row of answer.msg) echo(row.message)
        return
      }
      echo('No history.')
    } catch {
      echo('The history could not be read.', 'err')
    }
  }

  const body = (
    <div className="panel panel-default">
      <div className="panel-heading cmd-terminal-head">
        <span>{title}</span>
        <span className="cmd-terminal-tools">
          <button
            type="button"
            className="btn btn-xs btn-default history-terminal"
            data-device={deviceId}
            title="The commands sent to this machine before"
            onClick={() => void showHistory()}
          >
            <i className="fas fa-history"></i> History
          </button>
          <button
            type="button"
            className="btn btn-xs btn-default clear-terminal"
            title="Clear the screen"
            onClick={() => setLines([])}
          >
            <i className="fas fa-eraser"></i> Clear
          </button>
        </span>
      </div>
      <div className="panel-body cmd-terminal-body">
        {/* Oriunde dai clic in terminal, cursorul de scris e in linia de comanda -
            ca intr-un terminal adevarat, nu doar cand nimeresti casuta
            (Roxana, 14 septembrie 2026). Selectia de text ramane: un clic
            cu text selectat nu muta focusul, ca sa se poata copia. */}
        <div
          ref={box}
          className={`cmd-terminal${pane.key === 'powershell' ? ' is-powershell' : ''}`}
          id={`terminal_${deviceId}`}
          data-name={deviceId}
          onMouseUp={() => {
            if (window.getSelection()?.toString()) return
            box.current?.querySelector<HTMLInputElement>('input.cmd-input')?.focus()
          }}
        >
          {lines.map((line) => (
            <div key={line.key} className={`cmd-line cmd-line-${line.tone}`}>
              {line.text}
            </div>
          ))}
          <div className="cmd-entry">
            <span className="cmd-prompt">{promptText}</span>
            <input
              type="text"
              className="cmd-input"
              spellCheck={false}
              autoComplete="off"
              aria-label="Command line"
              disabled={busy || !pane.can_send}
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  submit()
                  return
                }
                /* Sagetile umbla prin ce s-a scris in fereastra asta, ca la
                   orice terminal. */
                if (e.key === 'ArrowUp' && past.length > 0) {
                  e.preventDefault()
                  const at = pastAt < 0 ? past.length - 1 : Math.max(0, pastAt - 1)
                  setPastAt(at)
                  setTyped(past[at])
                }
                if (e.key === 'ArrowDown' && pastAt >= 0) {
                  e.preventDefault()
                  const at = pastAt + 1
                  if (at >= past.length) {
                    setPastAt(-1)
                    setTyped('')
                  } else {
                    setPastAt(at)
                    setTyped(past[at])
                  }
                }
              }}
            />
          </div>
        </div>
      </div>
    </div>
  )

  if (!pane.sections) {
    return <div className="cmd-pane">{body}</div>
  }

  return (
    <div className="row cmd-pane">
      <div className="col-md-6">
        {body}
        {linux && (
          <p className="text-muted cmd-terminal-foot">
            The working directory is kept between lines, so <code>cd /etc</code> then <code>ls</code> lists that
            folder. This is still not a login shell: environment variables do not carry over.
          </p>
        )}
      </div>
      <div className="col-md-6">
        <DeviceCmdSections
          sections={sections}
          note={sectionsNote}
          scope={linux ? 'linux' : 'windows'}
          deviceId={deviceId}
          canSend={pane.can_send}
          onRun={runCommand}
        />
      </div>
    </div>
  )
}

/** `__OD_CWD__=` din coada raspunsului: nu se arata, muta promptul. */
function applyCwdMark(text: string): { text: string; cwd: string } {
  const value = text ?? ''
  const match = value.match(/(?:\r?\n)?__OD_CWD__=(.*?)\s*$/)
  if (!match) return { text: value.trim(), cwd: '' }

  return { text: value.replace(/(?:\r?\n)?__OD_CWD__=.*\s*$/, '').trim(), cwd: match[1].trim() }
}
